import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiPhone, FiMail } from 'react-icons/fi';
import { COMPANY_INFO, CONTACT_INFO, NAV_LINKS } from '@utils/constants';
import Button from '@components/common/Button';

/**
 * Header Component
 * Main site header with logo, navigation, and mobile menu
 */
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* Top Bar - Contact Info */}
      <div className="bg-bukawa-primary text-white py-2 hidden lg:block">
        <div className="container-custom">
          <div className="flex items-center justify-between text-sm">
            {/* Left - Contact Info */}
            <div className="flex items-center gap-6">
              <a
                href={`tel:${CONTACT_INFO.phone1}`}
                className="flex items-center gap-2 hover:text-bukawa-secondary transition-colors"
              >
                <FiPhone className="h-4 w-4" />
                <span>{CONTACT_INFO.phone1}</span>
              </a>
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center gap-2 hover:text-bukawa-secondary transition-colors"
              >
                <FiMail className="h-4 w-4" />
                <span>{CONTACT_INFO.email}</span>
              </a>
            </div>

            {/* Right - Tagline */}
            <div className="text-bukawa-secondary font-medium">
              {COMPANY_INFO.tagline}
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`
          sticky top-0 z-40 w-full transition-all duration-300
          ${
            isScrolled
              ? 'bg-white shadow-md'
              : 'bg-white lg:bg-transparent'
          }
        `}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20 lg:h-24">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-3 group"
              aria-label="Bukawa ACE Ltd Home"
            >
              <img
                src="/Bukawalogo.jpeg"
                alt="Bukawa ACE Ltd Logo"
                className="h-12 lg:h-16 w-auto transition-transform group-hover:scale-105"
              />
              <div className="hidden sm:block">
                <div className="font-heading font-bold text-bukawa-primary text-lg lg:text-xl leading-tight">
                  {COMPANY_INFO.shortName}
                </div>
                <div className="text-xs lg:text-sm text-bukawa-gray-600">
                  {COMPANY_INFO.tagline}
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {NAV_LINKS.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.id}
                    to={link.path}
                    className={`
                      text-sm font-medium transition-colors relative
                      ${
                        isActive
                          ? 'text-bukawa-primary'
                          : isScrolled
                          ? 'text-bukawa-gray-700 hover:text-bukawa-primary'
                          : 'text-white hover:text-bukawa-secondary'
                      }
                    `}
                  >
                    {link.name}
                    {isActive && (
                      <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-bukawa-secondary" />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* CTA Button - Desktop */}
            <div className="hidden lg:block">
              <Button
                to="/contact"
                variant="secondary"
                size="md"
              >
                Get Quote
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-bukawa-primary hover:bg-bukawa-light transition-colors"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <FiX className="h-6 w-6" />
              ) : (
                <FiMenu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`
          fixed inset-0 z-50 lg:hidden transition-all duration-300
          ${
            isMobileMenuOpen
              ? 'visible opacity-100'
              : 'invisible opacity-0'
          }
        `}
      >
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Menu Panel */}
        <div
          className={`
            absolute top-0 right-0 bottom-0 w-full max-w-sm
            bg-white shadow-2xl transition-transform duration-300
            ${
              isMobileMenuOpen
                ? 'translate-x-0'
                : 'translate-x-full'
            }
          `}
        >
          {/* Menu Header */}
          <div className="flex items-center justify-between p-6 border-b border-bukawa-gray-200">
            <div className="flex items-center gap-3">
              <img
                src="/Bukawalogo.jpeg"
                alt="Bukawa ACE Ltd"
                className="h-10 w-auto"
              />
              <div>
                <div className="font-heading font-bold text-bukawa-primary text-base">
                  {COMPANY_INFO.shortName}
                </div>
                <div className="text-xs text-bukawa-gray-600">
                  {COMPANY_INFO.tagline}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 rounded-lg text-bukawa-gray-600 hover:bg-bukawa-light transition-colors"
              aria-label="Close mobile menu"
            >
              <FiX className="h-6 w-6" />
            </button>
          </div>

          {/* Menu Items */}
          <nav className="flex flex-col p-6 space-y-2">
            {NAV_LINKS.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.id}
                  to={link.path}
                  className={`
                    flex items-center px-4 py-3 rounded-lg font-medium transition-colors
                    ${
                      isActive
                        ? 'bg-bukawa-primary text-white'
                        : 'text-bukawa-gray-700 hover:bg-bukawa-light'
                    }
                  `}
                >
                  {link.name}
                </Link>
              );
            })}
          </nav>

          {/* Contact Info */}
          <div className="px-6 py-4 border-t border-bukawa-gray-200">
            <div className="space-y-3">
              <a
                href={`tel:${CONTACT_INFO.phone1}`}
                className="flex items-center gap-3 text-bukawa-gray-700 hover:text-bukawa-primary transition-colors"
              >
                <FiPhone className="h-5 w-5" />
                <span className="text-sm">{CONTACT_INFO.phone1}</span>
              </a>
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center gap-3 text-bukawa-gray-700 hover:text-bukawa-primary transition-colors"
              >
                <FiMail className="h-5 w-5" />
                <span className="text-sm">{CONTACT_INFO.email}</span>
              </a>
            </div>

            {/* CTA Button */}
            <div className="mt-6">
              <Button
                to="/contact"
                variant="primary"
                fullWidth
              >
                Get a Quote
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;